import { useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, SvgIcon, TextField,
    Typography, CircularProgress, Tooltip
} from '@mui/material';
import { toast } from 'react-toastify';
import { ulid } from 'ulidx';
import { AudioFileVersion } from '@aviation/cabinapp-cabincast-sdk';
import { ReactComponent as CrossIcon } from '../../../icons/cross.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { AUDIO_FILE_LANGUAGES, uploadAudioFileVersion } from './AudioFile';

export interface IAudioFileVersionUploadDialogProps {
    open: boolean;
    files: File[];
    audioFileId: string;
    /** Languages that already have a version. */
    usedLanguages: string[];
    onClose: () => void;
    /** Receives the uploaded versions and persists them; should throw if persisting failed. */
    onUploaded: (versions: AudioFileVersion[]) => Promise<void>;
}

interface IUploadItem {
    key: string;
    file: File;
    language: string;
}

function AudioFileVersionUploadDialog({ open, files, audioFileId, usedLanguages, onClose, onUploaded }: IAudioFileVersionUploadDialogProps) {
    const [items, setItems] = useState<IUploadItem[]>([]);
    const [saving, setSaving] = useState(false);

    const used = useMemo(() => usedLanguages.map(l => l.toUpperCase()), [usedLanguages]);

    const addFiles = (current: IUploadItem[], added: File[]) => {
        const taken = [...used, ...current.map(i => i.language)];
        return [...current, ...added.map(file => {
            const language = AUDIO_FILE_LANGUAGES.find(l => !taken.includes(l.code))?.code ?? '';
            taken.push(language);
            return { key: ulid(), file, language };
        })];
    };

    useEffect(() => {
        if (!open) return;
        setItems(current => {
            const known = new Set(current.map(i => i.file));
            const added = files.filter(f => !known.has(f));
            return added.length === 0 ? current : addFiles(current, added);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, files]);

    useEffect(() => {
        if (!open) setItems([]);
    }, [open]);

    const errors = useMemo(() => {
        const result: string[] = [];
        if (items.length === 0) result.push('Select at least one file.');
        const languages = items.map(i => i.language);
        if (languages.some(l => l === '')) result.push('Select a language for every file.');
        if (languages.some(l => used.includes(l))) result.push('A version for this language already exists.');
        if (new Set(languages).size !== languages.length) result.push('Each language can only be used once.');
        return result;
    }, [items, used]);

    const handleSave = async () => {
        if (errors.length > 0 || saving) return;
        setSaving(true);
        try {
            const versions = await Promise.all(items.map(i => uploadAudioFileVersion(audioFileId, i.file, i.language)));
            await onUploaded(versions);
        } catch (e) {
            console.error(e);
            toast.error('An error occurred while uploading the audio file.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='sm'>
            <DialogTitle sx={{ m: 0, p: 2 }}>
                Add language versions
                <IconButton aria-label='close' disabled={saving} onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <SvgIcon component={CrossIcon} inheritViewBox />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {items.map(item => (
                        <Stack key={item.key} direction='row' spacing={1} alignItems='center'>
                            <Typography noWrap sx={{ flexGrow: 1, minWidth: 0 }} title={item.file.name}>{item.file.name}</Typography>
                            <TextField select size='small' sx={{ width: 170 }} value={item.language}
                                onChange={e => setItems(items.map(i => i.key === item.key ? { ...i, language: e.target.value } : i))}>
                                {AUDIO_FILE_LANGUAGES.map(l =>
                                    <MenuItem key={l.code} value={l.code} disabled={used.includes(l.code)}>{l.label} ({l.code})</MenuItem>)}
                            </TextField>
                            <Tooltip title='Remove file'>
                                <span>
                                    <IconButton color='error' disabled={saving} onClick={() => setItems(items.filter(i => i.key !== item.key))}>
                                        <SvgIcon component={TrashIcon} inheritViewBox />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    ))}
                    {errors.map(e => <Alert key={e} severity='error'>{e}</Alert>)}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant='contained' onClick={handleSave} disabled={saving || errors.length > 0}
                    startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}>Upload</Button>
            </DialogActions>
        </Dialog>
    );
}

export default AudioFileVersionUploadDialog;
